// The signed-in hub inside the home modal: Discover · Recent Seeds · Live
// Jams, and the Remix chooser a seed's Remix opens. Panels only — the modal
// (pages/HomePage.tsx) owns the frame, the tabs, the way back and where
// things go; the panels say what was picked.
//
// Discover and Recent Seeds are one carousel, as the extension's hub is: a
// seed at a time, big, with its record peeking out behind it. Each card
// previews on its own <audio>, never the app's player bar — the Flip's room
// is the page here, and the card stops it before it plays.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Ellipsis, ExternalLink } from "lucide-react";
import { participantKey, type FeedCard, type FeedPage, type SearchResult, type SeedSummary } from "@shared/api-types.js";
import { webApi } from "@web-shared/lib/web-api";
import { relativeTime } from "@web-shared/lib/format";
import { cn } from "@web-shared/lib/utils";
import { profilePath } from "@/lib/ctas";
import { groupMySeeds, previewOf } from "@/lib/my-seeds";
import { LiveGlyph, RecordGlyph, SproutGlyph } from "@/components/glyphs";
import { Carousel, ExpandButton, type CardActions, type HubSeed } from "./HubCarousel";

export type { CardActions, HubSeed };
import { useIsNarrow } from "@web-shared/hooks/useIsNarrow";
import { useSession } from "../session";
import { useLiveJams, jamPath, type LiveJam } from "../live";
import { Avatar } from "./Avatar";

export type HubTab = "discover" | "recent" | "live";

/** `short` is the phone's label: three tabs fit beside the mark and × only short. */
export const HUB_TABS: { id: HubTab; label: string; short: string }[] = [
  { id: "discover", label: "Discover", short: "Discover" },
  { id: "recent", label: "Recent Seeds", short: "Recent" },
  { id: "live", label: "Live Jams", short: "Live" },
];

export function isHubTab(v: string | null): v is HubTab {
  return v === "discover" || v === "recent" || v === "live";
}

export const DISCORD = "https://seed-bed.com/join";

function fromFeed(card: FeedCard, flipId: string | null): HubSeed {
  return {
    id: card.id,
    title: card.title,
    bpm: card.bpm,
    key: card.key,
    genre: card.genre,
    who: card.producer.username,
    badge: card.id === flipId ? { text: "★ This week's Flip", tone: "flip" } : { text: relativeTime(card.createdAt), tone: "time" },
    audioUrl: card.audioUrl || null,
  };
}

// ---------------------------------------------------------------------------
// Discover
// ---------------------------------------------------------------------------

type Sort = "discover" | "new";

export function DiscoverPanel({
  flip,
  actions,
  onUpload,
  initialQuery = "",
}: {
  /** This week's Flip: first in Hot, badged wherever it shows up. */
  flip: FeedCard | null;
  actions: CardActions;
  onUpload?: () => void;
  /** A search carried in from an old /search link. */
  initialQuery?: string;
}) {
  const [sort, setSort] = useState<Sort>("discover");
  const [q, setQ] = useState(initialQuery);
  const term = useDebounced(q.trim(), 250);
  const feed = useQuery({
    queryKey: ["web", "feed", sort, "hub"],
    enabled: term === "",
    queryFn: () => webApi<FeedPage>(`/feed?tab=${sort}`),
  });
  const found = useQuery({
    queryKey: ["web", "search", term],
    enabled: term !== "",
    queryFn: () => webApi<SearchResult>(`/search?q=${encodeURIComponent(term)}`),
  });
  const flipId = flip?.id ?? null;
  const seeds = useMemo(() => {
    const cards = (term === "" ? feed.data?.items : found.data?.seeds) ?? [];
    const lead = term === "" && sort === "discover" && flip && feed.data ? [flip, ...cards.filter((c) => c.id !== flip.id)] : cards;
    return lead.map((c) => fromFeed(c, flipId));
  }, [term, sort, feed.data, found.data, flip, flipId]);
  const producers = term === "" ? [] : (found.data?.users ?? []);
  const pending = term === "" ? feed.isPending : found.isPending;

  return (
    <Carousel
      key={`${sort}|${term}`}
      seeds={seeds}
      pending={pending}
      actions={actions}
      onUpload={onUpload}
      controls={
        <>
          <SearchField value={q} onChange={setQ} placeholder="Search seeds or producers" />
          <Chips value={sort} onChange={setSort} options={[{ id: "discover", label: "Hot" }, { id: "new", label: "New" }]} />
        </>
      }
      above={
        producers.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {producers.slice(0, 6).map((p) => (
              <Link
                key={p.id}
                to={profilePath(p.handle)}
                className="flex items-center gap-1.5 rounded-full border border-white/10 py-1 pl-1 pr-3 text-xs hover:border-white/25"
              >
                <Avatar name={p.username} url={p.avatarUrl} className="size-5" />
                {p.username}
              </Link>
            ))}
          </div>
        )
      }
      empty={
        term !== "" ? (
          <EmptyState glyph={<RecordGlyph />} title={`Nothing called “${term}”`} />
        ) : (
          <EmptyState glyph={<SproutGlyph />} title="No seeds yet" action={onUpload && { label: "Plant the first", onClick: onUpload }} />
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Recent Seeds: yours
// ---------------------------------------------------------------------------

type MineSort = "recent" | "az";

export function RecentPanel({
  actions,
  onUpload,
  onRemixFlip,
  onSignIn,
}: {
  actions: CardActions;
  onUpload: () => void;
  /** Empty: the other way in — remix the Flip. Absent when there's no Flip. */
  onRemixFlip?: () => void;
  onSignIn: () => void;
}) {
  const session = useSession();
  const [sort, setSort] = useState<MineSort>("recent");
  const [q, setQ] = useState("");
  const mine = useQuery({
    queryKey: ["web", "mine"],
    enabled: session !== null,
    staleTime: 30 * 60_000,
    queryFn: () => webApi<SeedSummary[]>("/seeds?mine=1"),
  });
  const seeds = useMemo(() => {
    const term = q.trim().toLowerCase();
    const groups = groupMySeeds(mine.data ?? []).filter((g) => term === "" || g.latest.title.toLowerCase().includes(term));
    if (sort === "az") groups.sort((a, b) => a.latest.title.localeCompare(b.latest.title));
    return groups.map((g): HubSeed => {
      const remixes = g.latest.remixCount ?? 0;
      return {
        id: g.latest.id,
        title: g.latest.title,
        bpm: g.latest.bpm,
        key: g.latest.key,
        genre: g.latest.genre,
        who: g.latest.producer.username,
        badge:
          remixes > 0
            ? { text: `↳ ${remixes} remix${remixes === 1 ? "" : "es"}`, tone: "remix" }
            : { text: relativeTime(g.latest.createdAt), tone: "time" },
        audioUrl: previewOf(g)?.url ?? null,
      };
    });
  }, [mine.data, q, sort]);

  if (!session) {
    return <EmptyState glyph={<Logo />} title="Your seeds live here" action={{ label: "Log in", onClick: onSignIn }} />;
  }
  const none = (mine.data ?? []).length === 0;
  return (
    <Carousel
      key={`${sort}|${q}`}
      seeds={seeds}
      pending={mine.isPending}
      actions={actions}
      onUpload={onUpload}
      controls={
        <>
          <SearchField value={q} onChange={setQ} placeholder="Search my seeds" />
          <Chips value={sort} onChange={setSort} options={[{ id: "recent", label: "Recent" }, { id: "az", label: "A–Z" }]} />
        </>
      }
      empty={
        none ? (
          <EmptyState
            glyph={<Logo />}
            title="Nothing planted yet"
            action={{ label: "Plant a seed", onClick: onUpload }}
            second={onRemixFlip ? { label: "Remix the Flip", onClick: onRemixFlip } : undefined}
          />
        ) : (
          <EmptyState glyph={<RecordGlyph />} title={`None of yours match “${q.trim()}”`} />
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Live Jams
// ---------------------------------------------------------------------------

export function LivePanel({
  onStart,
  onJoin,
  onJamOnFlip,
}: {
  /** Start a jam: what are we jamming on? */
  onStart: () => void;
  onJoin: (jam: LiveJam) => void;
  /** Empty: start one straight on the Flip. Absent when there's no Flip. */
  onJamOnFlip?: () => void;
}) {
  const live = useLiveJams();
  return (
    <div className="flex min-h-[22rem] flex-col">
      <div className="mb-4 flex items-center gap-2 sm:gap-3">
        <h3 className="text-2xl font-black tracking-tight sm:text-3xl">Live</h3>
        {live.length > 0 && (
          <span className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
            <span className="size-1.5 animate-pulse rounded-full bg-primary" />
            {live.length} {live.length === 1 ? "jam" : "jams"}
          </span>
        )}
        {/* Nobody live: the empty state carries the start, so the screen
            still has one mint button. */}
        {live.length > 0 && (
          <button
            type="button"
            onClick={onStart}
            className="ml-auto flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground shadow-glow hover:bg-white sm:px-5"
          >
            <span className="size-2 rounded-full bg-primary-foreground" /> Start a jam
          </button>
        )}
        <span className={cn("sm:hidden", live.length === 0 && "ml-auto")}>
          <ExpandButton />
        </span>
      </div>
      {live.length === 0 ? (
        <EmptyState
          glyph={<LiveGlyph />}
          title="Nobody's live right now"
          action={onJamOnFlip ? { label: "Start one on the Flip", onClick: onJamOnFlip } : { label: "Start a jam", onClick: onStart }}
          second={onJamOnFlip ? { label: "Pick a seed", onClick: onStart } : undefined}
        />
      ) : (
        <div className="space-y-2">
          {live.map((jam) => (
            <div key={jam.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-3 sm:gap-4 sm:p-4">
              <div className="flex shrink-0 -space-x-2">
                {jam.participants.slice(0, 3).map((p) => (
                  <Avatar key={participantKey(p)} name={p.username} url={null} className="size-9 ring-2 ring-[#141419]" />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold">{jam.title}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {jam.creatorUsername} · {jam.participants.length} in the room
                </p>
              </div>
              <Link
                to={jamPath(jam.id)}
                className="hidden h-10 items-center rounded-full px-4 text-sm font-semibold text-muted-foreground hover:text-foreground sm:flex"
              >
                Listen in
              </Link>
              <button
                type="button"
                onClick={() => onJoin(jam)}
                className="h-10 rounded-full border border-white/15 px-5 text-sm font-bold hover:border-primary/60 hover:bg-primary/10"
              >
                Join
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="min-h-6 flex-1" />
      <a
        href={DISCORD}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1.5 border-t border-white/5 pt-4 text-sm text-muted-foreground hover:text-foreground"
      >
        Meet people on Discord <ExternalLink className="size-3.5" />
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Remix: which DAW
// ---------------------------------------------------------------------------

export type Daw = "browser" | "ableton" | "audiotool" | "other";

export function RemixChooser({ seedTitle, onPick }: { seedTitle: string; onPick: (daw: Daw) => void }) {
  return (
    <div>
      <p className="-mt-3 mb-5 truncate text-sm text-muted-foreground">{seedTitle}</p>
      <div className="grid gap-2.5">
        <DawRow icon={<img src="/seedbed-logo.webp" alt="" className="size-6" />} name="Right here" sub="In the browser, no install" onClick={() => onPick("browser")} primary />
        <DawRow icon={<img src="/ableton-icon.webp" alt="" className="h-4 w-auto invert" />} name="Ableton Live" sub="Stems land on tracks in your Set" onClick={() => onPick("ableton")} />
        <DawRow icon={<img src="/audiotool-icon.webp" alt="" className="size-6 rounded" />} name="Audiotool" sub="Free, in any desktop browser" onClick={() => onPick("audiotool")} />
        <DawRow icon={<Ellipsis className="size-5 text-muted-foreground" />} name="Other DAW" sub="FL Studio, Logic, Bitwig…" onClick={() => onPick("other")} />
      </div>
    </div>
  );
}

function DawRow({ icon, name, sub, primary = false, onClick }: { icon: ReactNode; name: string; sub: string; primary?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors",
        primary ? "border-primary/50 bg-primary/[0.07] hover:bg-primary/[0.12]" : "border-white/10 bg-white/[0.02] hover:border-white/25",
      )}
    >
      <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#0d0d12]">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold">{name}</span>
        <span className="block text-xs text-muted-foreground">{sub}</span>
      </span>
      <ChevronRight className="size-4 text-muted-foreground" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function EmptyState({
  glyph,
  title,
  action,
  second,
}: {
  glyph: ReactNode;
  title: string;
  action?: { label: string; onClick: () => void };
  second?: { label: string; onClick?: () => void; href?: string };
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-white/10 px-6 py-12 text-center">
      <span className="size-14 text-foreground/80 [&_svg]:size-full">{glyph}</span>
      <p className="text-lg font-bold">{title}</p>
      {(action || second) && (
        <div className="flex flex-wrap justify-center gap-2">
          {action && (
            <button type="button" onClick={action.onClick} className="h-11 rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground hover:bg-white">
              {action.label}
            </button>
          )}
          {second &&
            (second.href ? (
              <a href={second.href} target="_blank" rel="noreferrer" className="flex h-11 items-center gap-1.5 rounded-full border border-white/15 px-5 text-sm font-bold hover:border-white/30">
                {second.label} <ExternalLink className="size-3.5" />
              </a>
            ) : (
              <button type="button" onClick={second.onClick} className="h-11 rounded-full border border-white/15 px-5 text-sm font-bold hover:border-white/30">
                {second.label}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function SearchField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  // A phone has no arrows (it swipes), so search shares the row with the
  // sort chips — with a placeholder short enough to fit beside them.
  const narrow = useIsNarrow();
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={narrow ? "Search" : placeholder}
      aria-label={placeholder}
      className="h-10 min-w-0 flex-1 rounded-xl border border-white/10 bg-[#0d0d12] px-4 text-sm placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none"
    />
  );
}

function Chips<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { id: T; label: string }[] }) {
  return (
    <div className="flex shrink-0 rounded-xl border border-white/10 p-0.5" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn("h-8 rounded-lg px-3 text-xs font-bold", value === o.id ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The Seedbed mark, where a glyph would stand for "yours". */
function Logo() {
  return <img src="/seedbed-logo.webp" alt="" className="size-full object-contain" />;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

